// MyProjects.jsx
import React, { useState, useEffect } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { Dialog, Transition } from "@headlessui/react";
import { HiOutlinePlus, HiOutlineFolder } from "react-icons/hi";
import { FaRegFolderOpen } from "react-icons/fa";
import { AiOutlineProject } from "react-icons/ai";
import { db } from "../Auth/firebase";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

const MyProjects = () => {
  const [projects, setProjects] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser) {
      const q = query(
        collection(db, "projects"),
        where("assignees", "array-contains", currentUser.uid)
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const projectsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProjects(projectsList);
      });

      return () => unsubscribe();
    }
  }, [currentUser]);

  const handleNavigate = (projectId) => {
    navigate(`/projects/${projectId}`);
  };

  return (
    <div className="w-[60%] mx-auto p-8 bg-white shadow-lg rounded-lg">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">My Projects</h2>
        <button
          onClick={() => setIsDialogOpen(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <HiOutlinePlus className="mr-2" />
          Add Project
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project) => (
          <div
            key={project.id}
            className="p-5 bg-white shadow-lg rounded-lg cursor-pointer hover:shadow-2xl transition-transform transform hover:scale-105"
            onClick={() => handleNavigate(project.id)}
          >
            <div className="flex items-center justify-between mb-4">
              <FaRegFolderOpen className="text-blue-600 text-3xl" />
              <span className="bg-[#9999ff] text-white text-sm font-medium px-2 py-1 rounded">
                {project.category || "Uncategorized"}
              </span>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              {project.name}
            </h3>
            <p className="text-sm text-gray-600 mb-4">{project.description}</p>
            <div className="flex items-center text-sm text-gray-600">
              <AiOutlineProject className="text-gray-500 mr-2" />
              <span>{project.assignees.length} Assignees</span>
            </div>
          </div>
        ))}
      </div>
      <Transition appear show={isDialogOpen} as={React.Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-10 overflow-y-auto"
          onClose={() => setIsDialogOpen(false)}
        >
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-md p-8 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-bold text-gray-900"
                >
                  Add Project
                </Dialog.Title>
                <p className="mt-4 text-sm text-gray-500">
                  To add a new project, please navigate to the Add Project page.
                </p>
                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-blue-900 bg-blue-100 border border-transparent rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                    onClick={() => {
                      setIsDialogOpen(false);
                      navigate("/projects/add");
                    }}
                  >
                    Go to Add Project
                  </button>
                  <button
                    type="button"
                    className="ml-3 inline-flex justify-center px-4 py-2 text-sm font-medium text-red-900 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-red-500"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default MyProjects;
